import { useSyncExternalStore } from "react";
import type { ApiUser } from "@hive/sdk";

export type FriendBridgeAction =
	| { kind: "pick"; max?: number }
	| { kind: "request"; user: BridgeUser; playableId?: string | null; playableTitle?: string | null }
	| { kind: "openRequests" }
	| { kind: "openProfile"; userId: string };

export interface BridgeUser {
	userId?: string | null;
	handle?: string | null;
	displayName?: string | null;
	avatarUrl?: string | null;
}

type Resolver = (result: unknown) => void;

let currentAction: FriendBridgeAction | null = null;
let currentResolver: Resolver | null = null;
const listeners = new Set<() => void>();

function emit() {
	for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function snapshot() {
	return currentAction;
}

function requestAction(action: FriendBridgeAction): Promise<unknown> {
	if (currentResolver) currentResolver({ ok: false, reason: "superseded" });
	currentAction = action;
	return new Promise((resolve) => {
		currentResolver = resolve;
		emit();
	});
}

export function resolveFriendBridgeAction(result: unknown) {
	const resolver = currentResolver;
	currentAction = null;
	currentResolver = null;
	emit();
	resolver?.(result);
}

export function useFriendBridgeAction(): FriendBridgeAction | null {
	return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function pickFriend(max?: number): Promise<unknown> {
	return requestAction({ kind: "pick", max });
}

export function confirmFriendRequest(input: {
	user: BridgeUser;
	playableId?: string | null;
	playableTitle?: string | null;
}): Promise<unknown> {
	return requestAction({ kind: "request", ...input });
}

export function openFriendRequests(): Promise<unknown> {
	return requestAction({ kind: "openRequests" });
}

export function openFriendProfile(userId: string): Promise<unknown> {
	return requestAction({ kind: "openProfile", userId });
}

export function friendToApiUser(friend: {
	userId: string;
	handle: string;
	displayName: string | null;
	avatarUrl: string | null;
	bio: string;
	createdAt: string;
}): ApiUser {
	return {
		id: friend.userId,
		handle: friend.handle,
		displayName: friend.displayName,
		avatarUrl: friend.avatarUrl,
		avatarFrameUrl: null,
		bio: friend.bio,
		kind: "MEMBER",
		creatorTier: "L1",
		email: null,
		emailVerified: false,
		createdAt: friend.createdAt,
		locale: null,
	};
}
